// src/pages/GuestsInfoPage.tsx
import React, { useContext, useEffect, useMemo } from "react";
import { Box, Container, Grid, Typography } from "@mui/material";
import GuestCard from "../components/GuestCard";
import ContentContainer from "./ContentContainer";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AppContext } from "../context/AppContext";
import { hasAllMandatoryData } from "../utils/guestUtils";
import { fetchGuestById } from "../api/GuestService"; // <-- NEW

const GuestsInfoPage: React.FC = () => {
    const { state, setCurrentStep, dispatch } = useContext(AppContext) ?? {};
    const guests = useMemo(() => state?.guests || [], [state?.guests]);
    const adultCount = state?.adultCount || 0;

    // Split out overflow guests (rendered separately so the canonical grid stays clean). The
    // mapper marks overflow=true when the reservation counts shrank below a kind's filled
    // count — the guest is still on the reservation, we just don't have a canonical slot.
    const canonicalGuests = useMemo(() => guests.filter((g) => !g?.overflow), [guests]);
    const overflowGuests = useMemo(() => guests.filter((g) => g?.overflow), [guests]);

    const location = useLocation();
    const isGuestInfoPage = location.pathname.includes("/guest-info");
    const { t: tGuestsInfo } = useTranslation("guests_info");

    useEffect(() => {
        if (isGuestInfoPage) setCurrentStep(1);
    }, [isGuestInfoPage, setCurrentStep]);

    const needsHydration = useMemo(
        () =>
            guests.some(
                (g, index) =>
                    g &&
                    g.id != null &&
                    g.id > 0 &&
                    !g.checkInSubmissionId &&
                    String(g.id).length < 11 &&
                    !hasAllMandatoryData(g, undefined, { requireContact: index === 0 })
            ),
        [guests]
    );

    useEffect(() => {
        if (!isGuestInfoPage || !needsHydration) return;

        let cancelled = false;

        (async () => {
            const hydrated = await Promise.all(
                guests.map(async (g, index) => {
                    if (
                        g?.id != null &&
                        g.id > 0 &&
                        !g.checkInSubmissionId &&
                        String(g.id).length < 11 &&
                        !hasAllMandatoryData(g, undefined, { requireContact: index === 0 })
                    ) {
                        try {
                            const full = await fetchGuestById(String(g.id));
                            const completed = hasAllMandatoryData(full, undefined, {
                                requireContact: index === 0,
                            });
                            // ⬇️ do NOT override updateUser here; keep what fetchGuestById decided
                            return { ...g, ...full, completed };
                        } catch (e) {
                            console.warn("⚠️ Failed to hydrate guest", g.id, e);
                            return g;
                        }
                    }
                    return g;
                })
            );

            if (!cancelled) {
                dispatch?.({ type: "SET_GUESTS", payload: hydrated });
            }
        })();

        return () => {
            cancelled = true;
        };
        // guests is read inside the effect; listing it would refetch after every hydration write.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isGuestInfoPage, needsHydration]);

    return (
        <ContentContainer>
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                {/* Title Section */}
                <Container maxWidth="md" sx={{ textAlign: "center", mb: 7, mt: 3 }}>
                    <Typography
                        variant="h4"
                        sx={{
                            fontFamily: "'Dala Floda', serif",
                            fontSize: "2.5rem",
                            mb: 2,
                            textTransform: "uppercase",
                            color: "var(--oc-text)",
                        }}
                    >
                        {tGuestsInfo("guests")}
                    </Typography>
                    <Typography variant="body1" sx={{ color: "var(--oc-text-muted)" }}>
                        {tGuestsInfo("guests_subheader")}
                    </Typography>
                </Container>

                {/* Guest Cards */}
                <Container maxWidth="lg">
                    <Grid container spacing={3} justifyContent="center" alignItems="flex-start">
                        {canonicalGuests.map((guest, index) => {
                            // Prefer the kind the mapper resolved (persisted ADULT/CHILD or
                            // DOB-derived) so the badge stays correct even when the absolute slot
                            // boundary moves under us. Fall back to the legacy index-based rule
                            // for guests without a resolved kind.
                            const isChild = guest.guestKind
                                ? guest.guestKind === "CHILD"
                                : index >= adultCount;
                            const isComplete =
                                hasAllMandatoryData(guest, undefined, { requireContact: index === 0 }) ||
                                Boolean(guest.completed);
                            return (
                                <Grid
                                    item
                                    xs={12}
                                    sm={6}
                                    md={4}
                                    key={guest.id}
                                    sx={{
                                        display: "flex",
                                        justifyContent: { xs: "center", sm: "flex-start" }, // ✅ center on mobile
                                    }}
                                >
                                    <GuestCard
                                        firstName={guest.firstName}
                                        lastName={guest.lastName}
                                        completed={isComplete}
                                        index={index + 1}
                                        totalGuests={canonicalGuests.length}
                                        isChild={isChild}
                                    />
                                </Grid>
                            );
                        })}
                    </Grid>
                </Container>

                {overflowGuests.length > 0 && (
                    <Container maxWidth="lg" sx={{ mt: 6 }}>
                        <Typography
                            variant="body2"
                            sx={{ color: "var(--oc-text-muted)", textAlign: "center", mb: 2 }}
                        >
                            {tGuestsInfo("other_guests_on_reservation", {
                                defaultValue: "Other guests on this reservation",
                            })}
                        </Typography>
                        <Grid container spacing={3} justifyContent="center" alignItems="flex-start">
                            {overflowGuests.map((guest, idx) => {
                                const isChild = guest.guestKind === "CHILD";
                                const isComplete =
                                    hasAllMandatoryData(guest, undefined, { requireContact: false }) ||
                                    Boolean(guest.completed);
                                const index = canonicalGuests.length + idx + 1;
                                return (
                                    <Grid
                                        item
                                        xs={12}
                                        sm={6}
                                        md={4}
                                        key={guest.id}
                                        sx={{
                                            display: "flex",
                                            justifyContent: { xs: "center", sm: "flex-start" },
                                        }}
                                    >
                                        <GuestCard
                                            firstName={guest.firstName}
                                            lastName={guest.lastName}
                                            completed={isComplete}
                                            index={index}
                                            totalGuests={canonicalGuests.length + overflowGuests.length}
                                            isChild={isChild}
                                        />
                                    </Grid>
                                );
                            })}
                        </Grid>
                    </Container>
                )}
            </Box>
        </ContentContainer>
    );
};

export default GuestsInfoPage;